import type { MeResponse } from '@tabel/shared/schemas/auth';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeftRight,
  BookOpen,
  Building,
  CalendarCheck2,
  ChevronRight,
  ChevronsUpDown,
  LogOut,
  MapPin,
  Menu,
  MoreHorizontal,
  PanelLeft,
  Plus,
  Search,
  X,
} from 'lucide-react';
import { type MouseEvent, type ReactNode, useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useCrumbs, usePageFreshness } from '../lib/crumbs';
import { ruDateTime } from '../lib/format';
import { useLogout } from '../lib/logout';
import {
  NAV_PATH,
  type NavItem,
  directoryNavFor,
  isSettingsPath,
  navFor,
  rulesHref,
  settingsItemFor,
} from '../lib/nav';
import { type UserObject, roleOn, userObjects } from '../lib/objects';
import { capabilities } from '../lib/personnel-api';
import { type AppRole, ROLE_LABEL, homeOf, useRole } from '../lib/role';
import { pageRoutes } from '../routes.generated';
import { RoleSwitch } from './RoleSwitch';
import { UnsavedGuardProvider, useGuardedNav, useUnsavedGuard } from './UnsavedGuard';
import { AssistantButton, AssistantPanel, AssistantProvider, useAssistant } from './assistant/AssistantPanel';
import {
  Avatar,
  Badge,
  Button,
  Dropdown,
  IconButton,
  MenuItem,
  MenuSeparator,
  SidePanelHost,
  SwitcherButton,
  useOverSheetScreen,
  useSidePanelHost,
} from './ui';

const KEY = 'tabel.sidebar';
function readSidebar(): boolean {
  try {
    return localStorage.getItem(KEY) !== 'closed';
  } catch {
    return true;
  }
}

/** Страницы после входа — из `routes.generated` (`pnpm gen`): меню показывает только их. */
const AUTHED_PATHS = pageRoutes.filter((r) => r.access === 'authed').map((r) => r.path);

/**
 * Свежесть данных СКУД: зелёная точка, если последний проход моложе 2 ч (FR-005). На телефоне — точка и время. `site` —
 * плашка страницы площадки (FR-053): время её последней отметки, а не объекта пары.
 */
function Freshness({
  lastPassAt,
  tzOffset,
  site,
}: { lastPassAt: string | null; tzOffset: string; site?: string }) {
  const fresh = lastPassAt ? Date.now() - Date.parse(lastPassAt) < 2 * 3_600_000 : false;
  return (
    <span
      className="inline-flex items-center gap-2 rounded-full border border-line bg-surface px-2.5 py-1 text-[12.5px] whitespace-nowrap text-ink-2"
      title={site ? `Последняя отметка площадки «${site}»` : 'Время последнего прохода, полученного от СКУД'}
    >
      <span className={`h-2 w-2 shrink-0 rounded-full ${fresh ? 'bg-ok' : 'bg-warn'}`} aria-hidden />
      <span className="max-sm:sr-only">Данные СКУД по </span>
      <span className="font-medium text-ink tabular-nums">{ruDateTime(lastPassAt, tzOffset)}</span>
    </span>
  );
}

/**
 * Переключатель объекта с поиском (дизайн-система, SwitcherButton; 007 T022, research R18): объекты, где у пользователя
 * есть роль, галочка у текущего. Объект один — список прямо говорит, что других пока нет (MUST). Выбор другого объекта —
 * через защиту несохранённого: новая пара «роль + объект» (`X-Role`, `X-Object`), кэш запросов сбрасывает
 * `RoleProvider`, переход — на домашнюю страницу роли: страницы прежнего объекта (табель, черновик) на новом не открыть.
 * Роли на новом объекте нет — первая роль объекта.
 */
function ObjectSwitcher({ me }: { me: MeResponse }) {
  const [q, setQ] = useState('');
  const { role, setRole } = useRole();
  const navigate = useNavigate();
  const { guard } = useUnsavedGuard();
  const objects = userObjects(me);
  const needle = q.trim().toLowerCase();
  const found = objects.filter((o) => o.name.toLowerCase().includes(needle));
  const pick = (o: UserObject) => {
    if (o.id === me.object.id) return;
    guard(() => {
      const next = roleOn(o, role);
      setRole(next, o.id);
      navigate(homeOf(next));
    });
  };
  return (
    <Dropdown
      width="w-[272px]"
      trigger={({ open, toggle }) => (
        <SwitcherButton
          icon={Building}
          caption="Объект"
          label={me.object.name}
          open={open}
          onClick={toggle}
        />
      )}
    >
      {(close) => (
        <>
          <div className="-mt-1 flex items-center gap-2 border-b border-line px-3">
            <Search size={16} strokeWidth={1.75} className="text-ink-3" aria-hidden />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Найти объект…"
              aria-label="Найти объект"
              className="h-10 min-w-0 flex-1 bg-transparent text-[13.5px] text-ink placeholder:text-ink-3 focus:outline-none max-sm:text-base"
            />
          </div>
          <div className="max-h-[50dvh] overflow-y-auto py-1">
            {found.length > 0 ? (
              found.map((o) => (
                <MenuItem
                  key={o.id}
                  icon={MapPin}
                  checked={o.id === me.object.id}
                  onClick={() => {
                    close();
                    pick(o);
                  }}
                >
                  {o.name}
                </MenuItem>
              ))
            ) : (
              <p className="px-3 py-2 text-[13px] text-ink-3">Объект не найден.</p>
            )}
          </div>
          {objects.length === 1 && (
            <p className="border-t border-line px-3 pt-2 pb-1.5 text-[12px] text-ink-3">
              Другие объекты появятся после подключения их СКУД.
            </p>
          )}
        </>
      )}
    </Dropdown>
  );
}

/** Карточка пользователя внизу меню; меню открывается вверх (дизайн-система, «Каркас экрана»). */
function UserMenu({ me, onHowItWorks }: { me: MeResponse; onHowItWorks: () => void }) {
  const { role, setRole } = useRole();
  const navigate = useNavigate();
  const logout = useLogout();
  const { guard } = useUnsavedGuard();
  // Все другие роли объекта (004, research R18): на телефоне переключателя в шапке нет, роль меняется здесь.
  const others = me.user.roles.filter((r) => r !== role);
  const [only] = others;
  const goTo = (other: AppRole) => () =>
    guard(() => {
      setRole(other);
      navigate(homeOf(other));
    });
  return (
    <Dropdown
      side="top"
      trigger={({ open, toggle }) => (
        <button
          type="button"
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={toggle}
          className={`flex w-full items-center gap-2.5 rounded-control px-2 py-2 text-left transition-colors hover:bg-surface/70 ${open ? 'bg-surface/70' : ''}`}
        >
          <Avatar name={me.user.fullName} />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[13.5px] font-medium text-ink">{me.user.fullName}</span>
            <span className="flex items-center gap-1.5">
              <span className="truncate text-[12px] text-ink-3">{me.user.position}</span>
              <Badge tone="neutral">
                {me.user.roles.length > 0 ? ROLE_LABEL[role] : 'Справочник компании'}
              </Badge>
            </span>
          </span>
          <ChevronsUpDown size={16} strokeWidth={1.75} className="shrink-0 text-ink-3" aria-hidden />
        </button>
      )}
    >
      {(close) => (
        <>
          {me.user.roles.length > 0 && (
            <MenuItem
              icon={BookOpen}
              onClick={() => {
                close();
                onHowItWorks();
              }}
            >
              Как считаются часы
            </MenuItem>
          )}
          {others.length === 1 && only && (
            <MenuItem
              icon={ArrowLeftRight}
              onClick={() => {
                close();
                goTo(only)();
              }}
            >
              Перейти в роль «{ROLE_LABEL[only]}»
            </MenuItem>
          )}
          {/* Ролей несколько — группа с заголовком (дизайн-система, Dropdown): длинные подписи не обрезаются. */}
          {others.length > 1 && (
            <div role="group" aria-label="Перейти в роль">
              <MenuSeparator />
              <p
                aria-hidden
                className="px-3 pt-1.5 pb-1 text-[11px] leading-[1.4] font-semibold tracking-[0.06em] text-ink-3 uppercase"
              >
                Перейти в роль
              </p>
              {others.map((other) => (
                <MenuItem
                  key={other}
                  icon={ArrowLeftRight}
                  onClick={() => {
                    close();
                    goTo(other)();
                  }}
                >
                  {ROLE_LABEL[other]}
                </MenuItem>
              ))}
            </div>
          )}
          <MenuSeparator />
          <MenuItem
            icon={LogOut}
            onClick={() => {
              close();
              guard(() => void logout());
            }}
          >
            Выйти
          </MenuItem>
        </>
      )}
    </Dropdown>
  );
}

/**
 * Каркас по дизайн-системе («Один интерфейс для компьютера и телефона», «Каркас экрана»): меню на сиреневом
 * холсте, белый лист с шапкой, крошки, свежесть СКУД и переключатель ролей справа, версия сборки в углу.
 * До 900 px (max-tablet) меню выезжает слева по кнопке «Меню»; до 640 px (max-sm) внизу — нижняя навигация.
 * Меню — по роли (`lib/nav.ts`, таблица «Права ролей»): пункты, разрешённые роли и уже имеющие страницу.
 */
export function AppShell({ me, children }: { me: MeResponse; children: ReactNode }) {
  return (
    <UnsavedGuardProvider>
      {/* «Помощник» (006): кнопка в шапке листа и правая панель разговора с ИИ-агентом. */}
      <AssistantProvider me={me}>
        <Shell me={me}>{children}</Shell>
      </AssistantProvider>
    </UnsavedGuardProvider>
  );
}

function Shell({ me, children }: { me: MeResponse; children: ReactNode }) {
  const { role } = useRole();
  const crumbs = useCrumbs();
  const guarded = useGuardedNav();
  const location = useLocation();
  const [open, setOpen] = useState(readSidebar);
  // Выезжающее меню планшета и телефона; на компьютере не используется.
  const [mobileOpen, setMobileOpen] = useState(false);
  const aside = useRef<HTMLElement>(null);
  // Правая панель страницы (SidePanel): лежит поверх листа у правого края и лист не сдвигает.
  const panel = useSidePanelHost();
  // 901–1239 px при открытой панели левее неё таблице мало места (колонка ФИО шире), поэтому меню на это
  // время свёрнуто, как кнопкой «Скрыть меню», но без запоминания (T107). Нажатие человека важнее: «Показать меню»
  // показывает его с первого нажатия, «Скрыть меню» запоминается, как без панели (T128). Панель закрыта — меню
  // такое, как выбрал человек.
  const overSheet = useOverSheetScreen() && panel.open;
  const [showAnyway, setShowAnyway] = useState(false);
  useEffect(() => {
    if (!overSheet) setShowAnyway(false);
  }, [overSheet]);
  const menuShown = open && (!overSheet || showAnyway);

  // Переход закрывает выезжающее меню.
  // biome-ignore lint/correctness/useExhaustiveDependencies: реагируем только на смену адреса
  useEffect(() => setMobileOpen(false), [location.pathname, location.search]);
  // Esc закрывает выезжающее меню; при открытии фокус — на первый пункт.
  useEffect(() => {
    if (!mobileOpen) return;
    aside.current?.querySelector<HTMLElement>('a, button')?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mobileOpen]);
  // Раз в минуту: свежесть СКУД и версия сборки в шапке не должны устаревать, пока страница открыта.
  const health = useQuery({ queryKey: ['health'], queryFn: api.health, refetchInterval: 60_000 });
  // Страница площадки показывает в плашке свою площадку, а не объект пары (FR-053).
  const page = usePageFreshness();
  // Серый счётчик у «Табели» (003, FR-022): вопросы без ответа по табелям бригадира в работе. Тот же запрос, что
  // у списка табелей: данные общие.
  const list = useQuery({
    queryKey: ['timesheets', 'brigadier'],
    queryFn: api.list,
    enabled: role === 'brigadier' && me.user.roles.includes('brigadier'),
  });
  const openQuestions = Array.isArray(list.data)
    ? list.data
        .filter((t) => t.status === 'draft' || t.status === 'returned')
        .reduce((n, t) => n + (t.openQuestions ?? 0), 0)
    : 0;
  const counterOf = (n: NavItem) => (n.counter === 'openQuestions' ? openQuestions : 0);

  /**
   * Кнопка в шапке делает то, что написано на ней сейчас (T128): нажатие человека важнее автосворачивания.
   * «Скрыть меню» — свернуть и запомнить, и при панели поверх листа тоже. «Показать меню» — показать с первого
   * нажатия; меню было свёрнуто человеком — выбор запоминается, свёрнуто только панелью — запоминать нечего.
   */
  const toggle = () => {
    const show = !menuShown;
    setShowAnyway(show && overSheet);
    if (open === show) return;
    try {
      localStorage.setItem(KEY, show ? 'open' : 'closed');
    } catch {
      // хранилище недоступно — состояние живёт до перезагрузки
    }
    setOpen(show);
  };

  // «Помощник» (006) — только если помощник есть: право «ИИ-агент» и функция 006 включена.
  const assistant = useAssistant();
  const legacy = me.user.roles.length > 0;
  const directoryNav = directoryNavFor(capabilities(me));
  const nav = [
    ...directoryNav,
    ...(legacy
      ? navFor(role, AUTHED_PATHS).filter((n) => n.to !== NAV_PATH.assistant || assistant !== null)
      : []),
  ];
  const home = legacy ? homeOf(role) : (directoryNav[0]?.to ?? '/personnel');
  // «Настройки» (007 T037): внизу меню над карточкой пользователя, если у роли есть хотя бы один раздел.
  const settings = legacy ? settingsItemFor(role, AUTHED_PATHS) : null;
  // Экран «Настройки» — во весь лист, без полей страницы: навигация разделов от края до края (SettingsLayout).
  const bleed = isSettingsPath(location.pathname);
  // «Как считаются часы» — страница «Правила расчёта» (004, research R20); из табеля — со ссылкой назад к нему.
  const howHref = location.pathname.startsWith('/timesheets/')
    ? rulesHref({ from: location.pathname })
    : rulesHref();
  // Табель открывается из домашнего раздела роли («Табели», «На проверку») — этот раздел и подсвечен.
  const inSection = (to: string) => location.pathname.startsWith('/timesheets/') && to === homeOf(role);

  const version = `версия ${health.data?.version ?? '—'}`;

  return (
    <div className="flex h-dvh bg-gradient-to-b from-canvas to-canvas-2 max-tablet:bg-surface">
      {mobileOpen && (
        <button
          type="button"
          aria-label="Закрыть меню"
          onClick={() => setMobileOpen(false)}
          className="fixed inset-0 z-40 bg-backdrop tablet:hidden"
        />
      )}
      <aside
        ref={aside}
        aria-label="Меню"
        className={`w-[256px] shrink-0 flex-col px-3 pt-5 pb-3 ${menuShown ? 'tablet:flex' : 'tablet:hidden'} ${
          mobileOpen
            ? 'max-tablet:fixed max-tablet:inset-y-0 max-tablet:left-0 max-tablet:z-50 max-tablet:flex max-tablet:w-[280px] max-tablet:overflow-y-auto max-tablet:bg-gradient-to-b max-tablet:from-canvas max-tablet:to-canvas-2 max-tablet:pb-[max(12px,env(safe-area-inset-bottom))] max-tablet:shadow-pop'
            : 'max-tablet:hidden'
        }`}
      >
        <div className="mb-5 flex items-center gap-2.5">
          <Link
            to={home}
            onClick={guarded.link(home)}
            className="flex min-w-0 flex-1 items-center gap-2.5 px-2"
          >
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-control bg-accent text-white">
              <CalendarCheck2 size={18} strokeWidth={1.75} aria-hidden />
            </span>
            <span className="text-[18px] leading-none font-semibold tracking-[-0.01em]">Табель</span>
          </Link>
          {mobileOpen && (
            <IconButton
              icon={X}
              label="Закрыть меню"
              onClick={() => setMobileOpen(false)}
              className="tablet:hidden"
            />
          )}
        </div>

        {legacy && (
          <div className="mb-4">
            <ObjectSwitcher me={me} />
          </div>
        )}

        <nav className="space-y-0.5" aria-label="Разделы">
          {nav.length === 0 && (
            <p className="px-2.5 py-2 text-[13px] text-ink-3">Разделов для этой роли пока нет.</p>
          )}
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              onClick={guarded.link(n.to)}
              className={({ isActive }) =>
                `flex items-center gap-2.5 rounded-control px-2.5 py-2 text-[13.5px] font-medium transition-colors max-tablet:min-h-10 ${
                  isActive || inSection(n.to)
                    ? 'bg-surface text-accent shadow-card'
                    : 'text-ink-2 hover:bg-surface/60 hover:text-ink'
                }`
              }
            >
              <n.icon size={16} strokeWidth={1.75} aria-hidden />
              {n.label}
              {counterOf(n) > 0 && (
                <span
                  title={`Вопросов без ответа: ${counterOf(n)}`}
                  className="ml-auto text-[12px] font-medium text-ink-3 tabular-nums"
                >
                  {counterOf(n)}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="mt-auto">
          <button
            type="button"
            onClick={() => guarded.go(howHref)}
            className="flex w-full items-center gap-2.5 rounded-control px-2.5 py-2 text-[13.5px] font-medium text-ink-2 transition-colors hover:bg-surface/60 hover:text-ink max-tablet:min-h-10"
          >
            <BookOpen size={16} strokeWidth={1.75} aria-hidden />
            Как считаются часы
          </button>
          {settings && (
            <NavLink
              to={settings.to}
              onClick={guarded.link(settings.to)}
              className={({ isActive }) =>
                `mt-0.5 flex items-center gap-2.5 rounded-control px-2.5 py-2 text-[13.5px] font-medium transition-colors max-tablet:min-h-10 ${
                  isActive
                    ? 'bg-surface text-accent shadow-card'
                    : 'text-ink-2 hover:bg-surface/60 hover:text-ink'
                }`
              }
            >
              <settings.icon size={16} strokeWidth={1.75} aria-hidden />
              {settings.label}
            </NavLink>
          )}
          <hr className="my-3 border-line" />
          {role === 'brigadier' && legacy && (
            <Button
              variant="soft"
              icon={Plus}
              className="mb-3 w-full justify-start!"
              onClick={() => guarded.go('/brigadier?new=1')}
            >
              Новый табель
            </Button>
          )}
          <UserMenu me={me} onHowItWorks={() => guarded.go(howHref)} />
        </div>
      </aside>

      <div
        className={`flex min-w-0 flex-1 flex-col py-2 pr-2 transition-[padding] duration-[340ms] ease-drawer motion-reduce:transition-none max-tablet:p-0 ${menuShown ? '' : 'tablet:pl-2'} max-tablet:pl-0`}
      >
        <div
          data-sheet
          data-side-panel={panel.open ? 'open' : undefined}
          className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-sheet border border-line bg-surface max-tablet:rounded-none max-tablet:border-0"
        >
          <header className="flex h-[52px] shrink-0 items-center gap-2 border-b border-line px-3 max-sm:gap-1 max-sm:px-2">
            <IconButton
              icon={PanelLeft}
              label={menuShown ? 'Скрыть меню' : 'Показать меню'}
              onClick={toggle}
              className="max-tablet:hidden"
            />
            <IconButton
              icon={Menu}
              label="Меню"
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen(true)}
              className="tablet:hidden"
            />
            <nav aria-label="Хлебные крошки" className="flex min-w-0 items-center gap-1 text-[13.5px]">
              {crumbs.map((c, i) => (
                // На телефоне видна только текущая крошка.
                <span
                  key={`${c.label}-${i}`}
                  className={`flex min-w-0 items-center gap-1 ${i < crumbs.length - 1 ? 'max-sm:hidden' : ''}`}
                >
                  {i > 0 && (
                    <ChevronRight size={14} className="shrink-0 text-ink-3 max-sm:hidden" aria-hidden />
                  )}
                  {c.to ? (
                    <Link
                      to={c.to}
                      onClick={guarded.link(c.to)}
                      className="truncate text-ink-2 hover:text-ink"
                    >
                      {c.label}
                    </Link>
                  ) : (
                    <span className="truncate font-medium text-ink">{c.label}</span>
                  )}
                </span>
              ))}
            </nav>
            <div className="ml-auto flex items-center gap-3 max-sm:gap-1">
              {legacy &&
                (page === null ? (
                  <Freshness
                    lastPassAt={health.data?.lastPassAt ?? me.lastPassAt}
                    tzOffset={me.object.tzOffset}
                  />
                ) : (
                  page !== 'pending' && (
                    <Freshness lastPassAt={page.lastPassAt} tzOffset={page.tzOffset} site={page.site} />
                  )
                ))}
              {/* На телефоне роль меняется в меню пользователя («Перейти в роль…»). */}
              <div className="max-sm:hidden">
                <RoleSwitch roles={me.user.roles} />
              </div>
              {role === 'brigadier' && legacy && (
                <IconButton
                  icon={Plus}
                  label="Новый табель"
                  onClick={() => guarded.go('/brigadier?new=1')}
                  className="border border-line tablet:hidden"
                />
              )}
              <AssistantButton />
            </div>
          </header>
          <main className="min-h-0 flex-1 overflow-auto">
            {bleed ? (
              // Поля и ширину задаёт сам экран (`t-set-in`); снизу на телефоне — место под нижнюю навигацию.
              <div
                data-layout="bleed"
                className="flex min-h-full flex-col max-sm:pb-[calc(56px+env(safe-area-inset-bottom))]"
              >
                <SidePanelHost value={panel}>
                  {children}
                  <AssistantPanel />
                </SidePanelHost>
                <p className="pb-3 text-center text-[11px] text-ink-3 tabular-nums tablet:hidden">
                  {version}
                </p>
              </div>
            ) : (
              <div className="mx-auto max-w-[1440px] space-y-8 px-8 py-8 max-tablet:space-y-6 max-tablet:px-4 max-tablet:py-5 max-sm:pb-[calc(56px+24px+env(safe-area-inset-bottom))]">
                <SidePanelHost value={panel}>
                  {children}
                  <AssistantPanel />
                </SidePanelHost>
                <p className="text-center text-[11px] text-ink-3 tabular-nums tablet:hidden">{version}</p>
              </div>
            )}
          </main>
        </div>
        {/* Версия сборки — подвал страницы (ориентир `contentinfo`, axe `region`). */}
        <footer className="px-2 pt-1.5 text-right text-[11px] text-ink-3 tabular-nums max-tablet:hidden">
          {version}
        </footer>
      </div>

      <BottomNav
        nav={nav.slice(0, 4)}
        inSection={inSection}
        onLink={guarded.link}
        onMore={() => setMobileOpen(true)}
        counterOf={counterOf}
      />
    </div>
  );
}

/** Нижняя навигация телефона (дизайн-система, BottomNav): разделы роли и «Ещё», которое открывает меню. */
function BottomNav({
  nav,
  inSection,
  onLink,
  onMore,
  counterOf,
}: {
  nav: ReturnType<typeof navFor>;
  inSection: (to: string) => boolean;
  /** Переход через защиту несохранённого. */
  onLink: (to: string) => (e: MouseEvent<HTMLAnchorElement>) => void;
  onMore: () => void;
  /** Серый счётчик у пункта. */
  counterOf: (n: NavItem) => number;
}) {
  const item =
    'flex min-h-12 flex-1 flex-col items-center justify-center gap-0.5 rounded-control text-[11px] font-medium';
  return (
    <nav
      aria-label="Основная навигация"
      className="fixed inset-x-0 bottom-0 z-30 hidden border-t border-line bg-surface px-1.5 pt-1 pb-[max(4px,env(safe-area-inset-bottom))] max-sm:flex"
    >
      {nav.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          onClick={onLink(n.to)}
          className={({ isActive }) =>
            `${item} ${isActive || inSection(n.to) ? 'text-accent' : 'text-ink-3'}`
          }
        >
          <span className="relative">
            <n.icon size={20} strokeWidth={1.75} aria-hidden />
            {counterOf(n) > 0 && (
              <span
                title={`Вопросов без ответа: ${counterOf(n)}`}
                className="absolute -top-1.5 left-full ml-0.5 rounded-full bg-subtle px-1 text-[10px] leading-[14px] font-semibold text-ink-3 tabular-nums ring-1 ring-line"
              >
                {counterOf(n)}
              </span>
            )}
          </span>
          {n.label}
        </NavLink>
      ))}
      <button type="button" onClick={onMore} className={`${item} text-ink-3`}>
        <MoreHorizontal size={20} strokeWidth={1.75} aria-hidden />
        Ещё
      </button>
    </nav>
  );
}
